<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  filters: { type: Object, required: true },
  catalog: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['apply', 'clear']);
const { t } = useI18n();
const draft = ref({ ...props.filters });

watch(
  () => props.filters,
  value => {
    draft.value = { ...value };
  },
  { deep: true }
);

const activeCount = computed(() =>
  Object.values(draft.value).reduce((count, value) => {
    if (Array.isArray(value)) return count + value.length;
    return count + (value && value !== 'data_solicitacao_1' ? 1 : 0);
  }, 0)
);
const optionValue = option => option?.id ?? option?.value ?? '';
const optionLabel = option => option?.label ?? option?.name ?? '';
</script>

<template>
  <section
    class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
  >
    <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <LaisField :label="t('NUCLEO_WORK_ORDERS.FILTERS.ADDRESS')">
        <input
          v-model="draft.filtro_endereco"
          list="nucleo-work-order-addresses"
          type="search"
          class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
          @keyup.enter="emit('apply', { ...draft })"
        />
        <datalist id="nucleo-work-order-addresses">
          <option
            v-for="option in catalog.addresses || []"
            :key="optionValue(option)"
            :value="option.value || optionLabel(option)"
          />
        </datalist>
      </LaisField>
      <LaisField :label="t('NUCLEO_WORK_ORDERS.FILTERS.RESPONSIBLE')">
        <select
          v-model="draft.filtro_responsavel"
          multiple
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 py-2 text-sm"
        >
          <option
            v-for="option in catalog.responsibles || []"
            :key="optionValue(option)"
            :value="optionValue(option)"
          >
            {{ optionLabel(option) }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('NUCLEO_WORK_ORDERS.FILTERS.MANAGER')">
        <select
          v-model="draft.filtro_gerente"
          multiple
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 py-2 text-sm"
        >
          <option
            v-for="option in catalog.managers || []"
            :key="optionValue(option)"
            :value="optionValue(option)"
          >
            {{ optionLabel(option) }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('NUCLEO_WORK_ORDERS.FILTERS.ONUS_REQUEST')">
        <select
          v-model="draft.filtro_solicitacao_onus"
          multiple
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 py-2 text-sm"
        >
          <option
            v-for="option in catalog.onus_requests || []"
            :key="optionValue(option)"
            :value="optionValue(option)"
          >
            {{ optionLabel(option) }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('NUCLEO_WORK_ORDERS.FILTERS.DISTRICT_CERTIFICATES')">
        <select
          v-model="draft.filtro_certidoes_comarca"
          multiple
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 py-2 text-sm"
        >
          <option
            v-for="option in catalog.district_certificates || []"
            :key="optionValue(option)"
            :value="optionValue(option)"
          >
            {{ optionLabel(option) }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('NUCLEO_WORK_ORDERS.FILTERS.STORE')">
        <select
          v-model="draft.filtro_loja"
          multiple
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 py-2 text-sm"
        >
          <option
            v-for="option in catalog.stores || []"
            :key="optionValue(option)"
            :value="optionValue(option)"
          >
            {{ optionLabel(option) }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('NUCLEO_WORK_ORDERS.FILTERS.SORT')">
        <select
          v-model="draft.ordenacao"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
        >
          <option
            v-for="option in catalog.orders || []"
            :key="optionValue(option)"
            :value="optionValue(option)"
          >
            {{ optionLabel(option) }}
          </option>
        </select>
      </LaisField>
    </div>
    <div
      class="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-lais-border-soft pt-4"
    >
      <span class="mr-auto text-xs text-lais-text-muted">
        {{ t('NUCLEO_WORK_ORDERS.FILTERS.ACTIVE', { count: activeCount }) }}
      </span>
      <LaisPrimaryButton
        variant="ghost"
        :disabled="loading"
        @click="emit('clear')"
      >
        {{ t('NUCLEO_WORK_ORDERS.ACTIONS.CLEAR') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        :is-loading="loading"
        @click="emit('apply', { ...draft })"
      >
        {{ t('NUCLEO_WORK_ORDERS.ACTIONS.APPLY') }}
      </LaisPrimaryButton>
    </div>
  </section>
</template>
